import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, MessageCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Settings() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/auth/notifications").then(r => setEnabled(r.data.whatsapp_notifications_enabled)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/auth/notifications", { whatsapp_notifications_enabled: enabled });
      toast.success("Notification preferences updated");
    } catch (err) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Account</div>
        <h1 className="text-3xl tracking-tighter font-medium mt-1">Settings</h1>
      </div>

      <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center">
            <MessageCircle size={18} className="text-[#E63946]" />
          </div>
          <div>
            <div className="font-medium">WhatsApp Notifications</div>
            <div className="text-xs text-[#475569]">Toggle whether the platform sends business notifications (credential updates, alerts) to your WhatsApp number.</div>
          </div>
        </div>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="wa-toggle-label">
          <input type="checkbox" data-testid="wa-toggle" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            className="accent-[#E63946] h-5 w-5" disabled={loading} />
          <span className="text-sm">Enable WhatsApp notifications for {user?.name}</span>
        </label>
        <div className="text-[11px] text-[#64748B]">
          Note: OTPs and customer warranty messages are always sent regardless of this setting because they are transactional.
        </div>
        <button data-testid="settings-save" onClick={save} disabled={saving || loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm font-medium disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-md bg-[#FFFFFF] border border-[#E5E7EB] flex items-center justify-center">
            <Bell size={18} className="text-[#475569]" />
          </div>
          <div>
            <div className="font-medium">Profile</div>
            <div className="text-xs text-[#475569]">Read-only account details.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          {[["Name", user?.name], ["Role", user?.role], ["Mobile", "+91 " + user?.mobile], ["Email", user?.email]].map(([k, v], i) => (
            <div key={i}>
              <div className="text-[10px] text-[#64748B]">{k}</div>
              <div className="mt-0.5 text-slate-900">{v || "-"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
